// ─────────────────────────────────────────────
// src/components/Footer.jsx
// ─────────────────────────────────────────────
import { useState } from "react";
import { FaInstagram, FaGoogle, FaYelp, FaLinkedinIn } from "react-icons/fa";

const PRIVACY_SECTIONS = [
  {
    heading: null,
    body: 'Welcome to Mars Academy ("Company," "we," "our," or "us"). We are committed to protecting the privacy and security of our users, including parents, guardians, and students who participate in our educational programs. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and enroll in our programs.\n\nBy using our services, you agree to the terms of this Privacy Policy.',
  },
  {
    heading: "2. Information We Collect",
    subSections: [
      {
        sub: "a. Personal Information",
        items: ["Parent/Guardian full name", "Email address", "Phone number", "Billing and payment information", "Child's name and age"],
      },
      {
        sub: "b. Automatically Collected Information",
        items: ["IP address", "Browser type", "Device information", "Website usage data"],
      },
    ],
  },
  {
    heading: "3. How We Use Your Information",
    items: [
      "Provide and manage program registrations",
      "Process payments securely",
      "Communicate with you regarding sessions, updates, and support",
      "Improve our website and services",
      "Comply with legal obligations",
    ],
  },
  {
    heading: "4. Payment Processing",
    body: "All payments are processed through a secure third-party payment processor (e.g., Stripe). We do not store full payment card details on our servers.",
  },
  {
    heading: "5. Cookies & Tracking Technologies",
    body: "We use cookies and similar tracking technologies, including Google Analytics, to understand how visitors use our website and to help us improve our services. These tools may collect information such as your IP address, browser type, device information, and pages visited. We also use hCaptcha, a bot-detection service, on our login and account creation pages to help prevent automated abuse; hCaptcha may collect technical information such as your IP address and browser data as part of this process. See hCaptcha's Privacy Policy (hcaptcha.com/privacy) and Terms of Service (hcaptcha.com/terms) for more information. You can control or disable cookies through your browser settings, though some features of our website may not function properly without them.",
  },
  {
    heading: "6. Sharing of Information",
    body: "We do not sell or rent your personal information. We may share information with:",
    items: ["Trusted service providers who assist in operating our website", "Payment processors", "Legal authorities when required by law"],
  },
  {
    heading: "7. Children's Privacy",
    body: "We collect limited information about children strictly for enrollment and participation purposes. Parents or legal guardians must provide consent for any child's participation. We do not knowingly collect unnecessary personal data from children.",
  },
  {
    heading: "8. Data Security",
    body: "We implement reasonable technical and organizational measures to protect your data. However, no system is completely secure, and we cannot guarantee absolute security.",
  },
  {
    heading: "9. Your Rights",
    body: "You may request to:",
    items: ["Correct inaccurate information", "Delete your data, subject to legal requirements"],
  },
  {
    heading: null,
    body: "To exercise any of these rights, please contact us at info@marsacademy.ai.",
  },
  {
    heading: "10. Data Retention",
    body: "We retain your information only as long as necessary to provide our services and comply with legal obligations.",
  },
  {
    heading: "11. Changes to This Policy",
    body: "We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised effective date.",
  },
  {
    heading: "12. Contact Us",
    body: "If you have any questions about this Privacy Policy, please contact us at:",
    contact: "info@marsacademy.ai",
  },
];

const TERMS_SECTIONS = [
  {
    heading: null,
    body: "By accessing or using the Mars Academy website and services, you agree to be bound by these Terms of Use. If you do not agree, please do not use our services.",
  },
  {
    heading: "2. Eligibility",
    body: "This website and account creation are intended for use by parents and legal guardians. Children should only use this website under the supervision of a parent or guardian.",
  },
  {
    heading: "3. Services Description",
    body: "We provide educational technology programs for elementary and middle school students, including in-person and/or online sessions.",
  },
  {
    heading: "4. User Responsibilities",
    body: "You agree to:",
    items: [
      "Provide accurate and complete information",
      "Maintain the confidentiality of your account",
      "Ensure your child follows appropriate conduct during sessions",
    ],
  },
  {
    heading: "5. Photographs & Media",
    body: "Mars Academy may photograph or record sessions for educational and promotional purposes. By enrolling, you grant permission for your child to appear in such materials unless you notify us in writing at info@marsacademy.ai.",
  },
  {
    heading: "6. Payments and Refunds",
    body: "All fees must be paid at the time of registration unless otherwise specified. If your school's PTA handles registration, refunds are subject to your PTA's policies. If no PTA is handling registration, please contact us at info@marsacademy.ai — refund situations are reviewed individually. Missing a class is not considered a valid reason for a refund.",
  },
  {
    heading: "7. Cancellations & Force Majeure",
    body: "If a session must be cancelled or rescheduled due to school closures, weather, instructor unavailability, or other circumstances beyond our control, we will notify affected families and work to reschedule the session or provide credit where possible. Mars Academy is not liable for cancellations resulting from circumstances outside our reasonable control.",
  },
  {
    heading: "8. Intellectual Property",
    body: "All content, curriculum, materials, and branding are the property of Mars Academy and may not be copied, distributed, or reproduced without permission.",
  },
  {
    heading: "9. Limitation of Liability",
    body: 'To the fullest extent permitted by law, Mars Academy shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services.',
  },
  {
    heading: "10. Indemnification",
    body: "You agree to indemnify and hold Mars Academy, its instructors, and staff harmless from any claims, damages, or expenses arising from your or your child's conduct, or any property damage caused during participation in our programs.",
  },
  {
    heading: "11. Disclaimer",
    body: 'Our services are provided "as is" without warranties of any kind. We do not guarantee specific educational outcomes.',
  },
  {
    heading: "12. Termination",
    body: "We reserve the right to suspend or terminate access to our services for violations of these Terms.",
  },
  {
    heading: "13. Governing Law",
    body: "These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Virginia.",
  },
  {
    heading: "14. Changes to Terms",
    body: "We may update these Terms at any time. Continued use of the services constitutes acceptance of the revised Terms.",
  },
  {
    heading: "15. Contact Information",
    body: "For questions regarding these Terms, please contact:",
    contact: "info@marsacademy.ai",
  },
];

function LegalModal({ onClose, icon, title, accentColor, accentColorRgb, introLabel, sections }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1.5rem",
        animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "linear-gradient(160deg, #0f1b2d 0%, #0a1220 100%)",
          border: "1.5px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          width: "100%",
          maxWidth: 680,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: `0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(${accentColorRgb},0.15)`,
          overflow: "hidden",
          animation: "slideUp 0.25s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "1.8rem 2rem 1.4rem",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem",
          background: `linear-gradient(135deg, rgba(${accentColorRgb},0.12) 0%, rgba(6,182,212,0.06) 100%)`,
          flexShrink: 0,
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "0.4rem" }}>
              <span style={{ fontSize: "1.5rem" }}>{icon}</span>
              <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.5rem", color: "#fff" }}>{title}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem", fontWeight: 600 }}>Effective Date: April 21, 2026</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 10,
              color: "rgba(255,255,255,0.6)",
              cursor: "pointer",
              fontSize: "1.1rem",
              width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", padding: "1.8rem 2rem 2rem", flex: 1 }}>
          {/* Intro */}
          <div style={{
            background: `rgba(${accentColorRgb},0.08)`,
            border: `1px solid rgba(${accentColorRgb},0.18)`,
            borderRadius: 14,
            padding: "1.1rem 1.4rem",
            marginBottom: "1.8rem",
            color: "rgba(255,255,255,0.55)",
            fontSize: "0.87rem",
            lineHeight: 1.75,
            fontWeight: 600,
          }}>
            <strong style={{ color: accentColor, display: "block", marginBottom: "0.4rem" }}>{introLabel}</strong>
            {sections[0].body}
          </div>

          {sections.slice(1).map((sec, i) => (
            <div key={i} style={{ marginBottom: "1.6rem" }}>
              {sec.heading && (
                <div style={{
                  fontFamily: "'Fredoka One', cursive",
                  fontSize: "1rem",
                  color: "#06B6D4",
                  marginBottom: "0.6rem",
                  paddingBottom: "0.4rem",
                  borderBottom: "1px solid rgba(6,182,212,0.15)",
                }}>
                  {sec.heading}
                </div>
              )}
              {sec.body && (
                <p style={{ color: "rgba(255,255,255,0.52)", fontSize: "0.86rem", lineHeight: 1.75, fontWeight: 600, marginBottom: sec.items ? "0.6rem" : 0 }}>
                  {sec.body}
                </p>
              )}
              {sec.items && (
                <ul style={{ margin: 0, paddingLeft: "1.2rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  {sec.items.map((item, j) => (
                    <li key={j} style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.65 }}>{item}</li>
                  ))}
                </ul>
              )}
              {sec.subSections && sec.subSections.map((s, j) => (
                <div key={j} style={{ marginBottom: "0.9rem" }}>
                  <div style={{ color: "rgba(255,255,255,0.65)", fontWeight: 800, fontSize: "0.85rem", marginBottom: "0.4rem" }}>{s.sub}</div>
                  <ul style={{ margin: 0, paddingLeft: "1.2rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    {s.items.map((item, k) => (
                      <li key={k} style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.84rem", fontWeight: 600, lineHeight: 1.65 }}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {sec.contact && (
                <a href={`mailto:${sec.contact}`} style={{ color: "#10B981", fontWeight: 800, fontSize: "0.88rem", textDecoration: "none" }}>
                  {sec.contact}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Footer bar */}
        <div style={{
          padding: "1rem 2rem",
          borderTop: "1px solid rgba(255,255,255,0.07)",
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
          background: "rgba(0,0,0,0.2)",
        }}>
          <button
            onClick={onClose}
            style={{
              background: `linear-gradient(135deg, ${accentColor}, #06B6D4)`,
              border: "none",
              borderRadius: 50,
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.88rem",
              padding: "0.65rem 1.8rem",
              cursor: "pointer",
              boxShadow: `0 4px 18px rgba(${accentColorRgb},0.35)`,
              letterSpacing: "0.02em",
            }}
          >
            Got it
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0; transform:translateY(24px) } to { opacity:1; transform:translateY(0) } }
      `}</style>
    </div>
  );
}

function PrivacyModal({ onClose }) {
  return (
    <LegalModal
      onClose={onClose}
      icon="🔒"
      title="Privacy Policy"
      accentColor="#9B5CF6"
      accentColorRgb="155,92,246"
      introLabel="1. Introduction"
      sections={PRIVACY_SECTIONS}
    />
  );
}

function TermsModal({ onClose }) {
  return (
    <LegalModal
      onClose={onClose}
      icon="📄"
      title="Terms of Use"
      accentColor="#FFD93D"
      accentColorRgb="255,217,61"
      introLabel="1. Acceptance of Terms"
      sections={TERMS_SECTIONS}
    />
  );
}

export default function Footer({ setPage }) {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const cols = [
    {
      heading: "Explore",
      links: [["Program", "programs"], ["Before & After School Programs", "after-school-programs"], ["About Us", "about"], ["Teach With Us", "teach"]],
    },
    {
      heading: "Parents",
      links: [["Login", "login"], ["Dashboard", "dashboard"], ["FAQ", "faq"], ["Fairfax, VA Programs", "fairfax-va"]],
    },
  ];

  return (
    <>
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}

      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "3.5rem 2rem 2rem", marginTop: "2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "2.5rem", marginBottom: "2.5rem" }}>

            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "0.75rem" }}>
                <span style={{ fontSize: "1.4rem" }}>🚀</span>
                <span style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.35rem", color: "#FFD93D" }}>Mars Academy</span>
              </div>
              <p style={{ color: "rgba(255,255,255,0.38)", fontSize: "0.83rem", lineHeight: 1.7, fontWeight: 600, marginBottom: "1rem" }}>
                Launching young minds into a tech-forward future — one mission at a time.
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <a href="https://www.instagram.com/themarsacademy_edu/" target="_blank" rel="noopener noreferrer" aria-label="Mars Academy on Instagram" style={{ textDecoration: "none" }}>
                  <div style={{ position: "relative", width: 34, height: 34, borderRadius: 9, background: "#000", overflow: "hidden", cursor: "pointer", isolation: "isolate" }}>
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FaInstagram style={{ color: "#fff", fontSize: "1.2rem" }} />
                    </div>
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #fdf497 0%, #fd5949 40%, #d6249f 70%, #285AEB 100%)", mixBlendMode: "multiply", pointerEvents: "none" }} />
                  </div>
                </a>
                <a href="https://www.linkedin.com/company/mars-academy-llc/" target="_blank" rel="noopener noreferrer" aria-label="Mars Academy on LinkedIn" style={{ textDecoration: "none" }}>
                  <div style={{ position: "relative", width: 34, height: 34, borderRadius: 9, background: "#0A66C2", overflow: "hidden", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaLinkedinIn style={{ color: "#fff", fontSize: "1.05rem" }} />
                  </div>
                </a>
                <a href="https://share.google/Zq6cexJdlSWIiVyqu" target="_blank" rel="noopener noreferrer" aria-label="Mars Academy on Google" style={{ textDecoration: "none" }}>
                  <div style={{ position: "relative", width: 34, height: 34, borderRadius: 9, background: "#fff", overflow: "hidden", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaGoogle style={{ color: "#4285F4", fontSize: "1.05rem" }} />
                  </div>
                </a>
                <a href="https://www.yelp.com/biz/mars-academy-vienna" target="_blank" rel="noopener noreferrer" aria-label="Mars Academy on Yelp" style={{ textDecoration: "none" }}>
                  <div style={{ position: "relative", width: 34, height: 34, borderRadius: 9, background: "#d32323", overflow: "hidden", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaYelp style={{ color: "#fff", fontSize: "1.1rem" }} />
                  </div>
                </a>
              </div>
            </div>

            {/* Link columns */}
            {cols.map((col) => (
              <div key={col.heading}>
                <h4 style={{ fontFamily: "'Fredoka One', cursive", fontSize: "0.98rem", color: "#FFD93D", marginBottom: "0.85rem" }}>{col.heading}</h4>
                {col.links.map(([label, pg]) => (
                  <div key={label} style={{ marginBottom: "0.45rem" }}>
                    <button
                      onClick={() => {
                        const [pageName, hash] = pg.split("#");
                        setPage(pageName);
                        if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 100);
                      }}
                      style={{ background: "none", border: "none", color: "rgba(255,255,255,0.42)", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: "0.83rem", padding: 0, textAlign: "left", transition: "color 0.2s" }}
                      onMouseEnter={(e) => (e.target.style.color = "#fff")}
                      onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.42)")}
                    >
                      {label}
                    </button>
                  </div>
                ))}
              </div>
            ))}

            {/* Contact */}
            <div>
              <h4 style={{ fontFamily: "'Fredoka One', cursive", fontSize: "0.98rem", color: "#FFD93D", marginBottom: "0.85rem" }}>Contact</h4>
              {[["📧", "info@marsacademy.ai"], ["📞", "(202)-596-5089"], ["📍", "Fairfax, Virginia"]].map(([icon, val]) => (
                <div key={val} style={{ display: "flex", gap: 8, marginBottom: "0.55rem", alignItems: "center" }}>
                  <span style={{ fontSize: "0.85rem" }}>{icon}</span>
                  <span style={{ color: "rgba(255,255,255,0.42)", fontSize: "0.82rem", fontWeight: 600 }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "1.4rem", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.6rem", alignItems: "center" }}>
            <p style={{ color: "rgba(255,255,255,0.22)", fontSize: "0.78rem", fontWeight: 600 }}>© 2026 Mars Academy. All rights reserved.</p>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <button
                onClick={() => setShowPrivacy(true)}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: "0.78rem", padding: 0, transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "#9B5CF6"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}
              >
                Privacy Policy
              </button>
              <span style={{ color: "rgba(255,255,255,0.15)", fontSize: "0.78rem" }}>·</span>
              <button
                onClick={() => setShowTerms(true)}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: "0.78rem", padding: 0, transition: "color 0.2s" }}
                onMouseEnter={e => e.target.style.color = "#FFD93D"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}
              >
                Terms of Use
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
